/**
 * The Data import screens' view model (f8.2).
 *
 * Pure functions over what the server sent, for the same reason the backend's
 * plan builder is pure: the interesting parts -- which pill a batch gets, what
 * a change row says, which order the table is in -- are decisions, and a
 * decision buried in JSX is a decision nobody can test.
 *
 * **Nothing here derives a rule.** It does not decide what may be committed,
 * who may see a batch, or whether a slot is resolved; the server decided all
 * three and this file renders the answer. What it does is choose words and
 * pills, and put rows in an order.
 */

import type {
  IngestionBatchSummary,
  StagedLevelCounts,
  StagedNode,
  StagedRoleSlot,
} from '@/api/client';

/** The hierarchy, top-down. A child must never render above its parent. */
export const LEVEL_ORDER: readonly string[] = [
  'BUSINESS_UNIT_GROUP',
  'BUSINESS_UNIT',
  'CLIENT',
  'ENGAGEMENT',
];

const LEVEL_LABELS: Readonly<Record<string, string>> = {
  BUSINESS_UNIT_GROUP: 'Business Unit Group',
  BUSINESS_UNIT: 'Business Unit',
  CLIENT: 'Client',
  ENGAGEMENT: 'Engagement',
};

/** A level's human label, or the code itself if a later phase adds a fifth. */
export function levelLabel(level: string): string {
  return LEVEL_LABELS[level] ?? level;
}

const ACTION_PILLS: Readonly<Record<string, { label: string; pill: string }>> = {
  CREATE: { label: 'Create', pill: 'pill-success' },
  UPDATE: { label: 'Update', pill: 'pill-info' },
  UNCHANGED: { label: 'Unchanged', pill: 'pill-neutral' },
};

export function actionPresentation(action: string) {
  return ACTION_PILLS[action] ?? { label: action, pill: 'pill-neutral' };
}

/** How a batch state is spoken about, and which pill carries it. */
const STATE_PILLS: Readonly<Record<string, { label: string; pill: string }>> = {
  RECEIVED: { label: 'Received', pill: 'pill-neutral' },
  PARSING: { label: 'Parsing', pill: 'pill-info' },
  STAGED: { label: 'Staged', pill: 'pill-warning' },
  AWAITING_RESOLUTION: { label: 'Awaiting resolution', pill: 'pill-warning' },
  COMMITTED: { label: 'Committed', pill: 'pill-success' },
  DISCARDED: { label: 'Discarded', pill: 'pill-neutral' },
  REJECTED: { label: 'Rejected', pill: 'pill-danger' },
};

export function statePresentation(state: string) {
  // An unrecognised state renders neutrally with its own code rather than
  // breaking the screen: the vocabulary is a seeded table, not an enum.
  return STATE_PILLS[state] ?? { label: state, pill: 'pill-neutral' };
}

/** The states in which a batch is still waiting for somebody. */
export const OPEN_BATCH_STATES: readonly string[] = ['STAGED', 'AWAITING_RESOLUTION'];

export function isOpen(state: string): boolean {
  return OPEN_BATCH_STATES.includes(state);
}

export interface BatchOutcome {
  label: string;
  pill: string;
  /** The one line under the pill. Empty when there is nothing to add. */
  detail: string;
}

/**
 * The four outcome shapes the recent-batches table renders.
 *
 * A committed or staged batch shows what its plan proposes, a rejected one
 * shows the machine reason it was refused, and a discarded one shows the plan
 * it kept -- because the staged rows are retained precisely so an abandoned
 * decision stays inspectable.
 */
export function outcomeFor(batch: IngestionBatchSummary): BatchOutcome {
  const { label, pill } = statePresentation(batch.state);

  if (batch.state === 'REJECTED') {
    return { label, pill, detail: batch.rejection_reason ?? 'refused' };
  }

  const parts: string[] = [];
  if (batch.created_count) parts.push(`${batch.created_count} to create`);
  if (batch.updated_count) parts.push(`${batch.updated_count} to update`);
  if (batch.unchanged_count) parts.push(`${batch.unchanged_count} unchanged`);

  if (batch.state === 'DISCARDED') {
    return {
      label,
      pill,
      detail: parts.length ? `${parts.join(' · ')}, not written` : 'not written',
    };
  }
  if (batch.state === 'COMMITTED') {
    const written: string[] = [];
    if (batch.created_count) written.push(`${batch.created_count} created`);
    if (batch.updated_count) written.push(`${batch.updated_count} updated`);
    return { label, pill, detail: written.join(' · ') };
  }
  return { label, pill, detail: parts.join(' · ') };
}

export interface LevelTile {
  level: string;
  label: string;
  total: number;
  /** Nodes with no existing row behind them. */
  created: number;
  /** Nodes that resolved to an existing row, changed or not. */
  matched: number;
  /** The line under the number, when there is no `+N new` badge beside it. */
  foot: string;
}

/**
 * One tile per level, in hierarchy order, however the server listed them.
 *
 * The prototype's *"all matched"* and *"+1 new &middot; 5 matched"* are the
 * two shapes; a level with nothing staged gets a third rather than an empty
 * line, because a blank caption reads as a rendering fault.
 */
export function levelTiles(levels: readonly StagedLevelCounts[]): LevelTile[] {
  const byLevel = new Map(levels.map((entry) => [entry.level, entry]));
  return LEVEL_ORDER.filter((level) => byLevel.has(level)).map((level) => {
    const entry = byLevel.get(level) as StagedLevelCounts;
    const matched = entry.updated + entry.unchanged;
    return {
      level,
      label: entry.label,
      total: entry.total,
      created: entry.created,
      matched,
      foot: entry.created
        ? matched
          ? `${matched} matched`
          : ''
        : matched
          ? 'all matched'
          : 'nothing staged',
    };
  });
}

/**
 * The change table, ordered so a parent always precedes its children.
 *
 * The server already returns them in that order; sorting again here is not
 * distrust but independence -- the table's ordering is a property of the
 * table, and a later endpoint that paged differently must not silently put a
 * client above the unit it belongs to.
 */
export function orderedChanges(nodes: readonly StagedNode[]): StagedNode[] {
  return [...nodes].sort((left, right) => {
    const byLevel =
      LEVEL_ORDER.indexOf(left.level) - LEVEL_ORDER.indexOf(right.level);
    if (byLevel !== 0) return byLevel;
    return left.natural_key.localeCompare(right.natural_key);
  });
}

/** The values a change row explains, in the order a reader wants them. */
const DETAIL_FIELDS: readonly string[] = [
  'code',
  'name',
  'start_date',
  'end_date',
  'delivery_stage_code',
  'delivery_country_code',
  'reporting_currency_code',
];

function render(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'empty';
  return String(value);
}

/**
 * The `Detail` cell: what actually changes, or what is being created.
 *
 * An update renders the before-and-after pair the server stored --
 * `name: Old → New` -- rather than re-reading the entity, which is what
 * makes the cell agree with what will be committed.
 */
export function detailFor(node: StagedNode): string {
  if (node.action === 'UPDATE') {
    const changed = DETAIL_FIELDS.filter(
      (field) =>
        field in node.proposed_values &&
        render(node.proposed_values[field]) !== render(node.current_values[field]),
    ).map(
      (field) =>
        `${field}: ${render(node.current_values[field])} → ${render(
          node.proposed_values[field],
        )}`,
    );
    return changed.length ? changed.join(', ') : 'no field differs';
  }

  if (needsCode(node)) {
    return 'No code could be proposed — supply one before committing';
  }

  const code = node.proposed_values.code;
  if (typeof code === 'string' && code) {
    return node.code_is_proposed ? `Proposed code ${code}` : `Code ${code}`;
  }
  return `New ${levelLabel(node.level).toLowerCase()}`;
}

/**
 * Whether a node needs a code a human has to type.
 *
 * `code_is_proposed` with no proposed code is the server saying *"this level
 * requires a code, the workbook carries none, and the one I derived collided"*.
 * It is the single condition that blocks f8.3's commit on a node.
 */
export function needsCode(node: StagedNode): boolean {
  return (
    node.code_is_proposed === true &&
    (node.proposed_values.code === null || node.proposed_values.code === undefined)
  );
}

/** The `Appears on` cell: what a slot is attached to, and how widely. */
export function appearsOn(slot: StagedRoleSlot): string {
  if (slot.level === 'ENGAGEMENT') {
    return slot.source_row_count === 1
      ? '1 engagement'
      : `${slot.source_row_count} engagements`;
  }
  return slot.node_name;
}

const SLOT_PILLS: Readonly<Record<string, { label: string; pill: string }>> = {
  MATCHED: { label: 'Matched', pill: 'pill-success' },
  UNMATCHED: { label: 'No confident match', pill: 'pill-warning' },
  AMBIGUOUS: { label: 'More than one account', pill: 'pill-warning' },
  CONTRADICTED: { label: 'Workbook disagrees', pill: 'pill-danger' },
};

export function slotPresentation(state: string) {
  return SLOT_PILLS[state] ?? { label: state, pill: 'pill-neutral' };
}

/** What the `Suggested match` cell says when there is nothing to suggest. */
export function suggestedMatch(slot: StagedRoleSlot): string | null {
  return slot.matched_display_name;
}

/** `#0147`, the label the prototype renders. Never routable. */
export function batchLabel(batchNumber: number): string {
  return `#${String(batchNumber).padStart(4, '0')}`;
}

/** `95.9 KB`. Decimal, because that is what a file manager shows. */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1000;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

/** `a91f4c…7de2`. A checksum a human compares, not one they retype. */
export function shortChecksum(sha256: string): string {
  return sha256.length <= 14
    ? sha256
    : `${sha256.slice(0, 6)}…${sha256.slice(-4)}`;
}

/** `20 Sep 2026, 09:12`, in the reader's own zone. */
export function formatWhen(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  return when.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The row triple, explained rather than only counted.
 *
 * *"78 blank trailing rows"* is what the prototype draws, and the difference
 * between that and a bare `78` is whether the operator has to go and look.
 */
export function droppedExplanation(
  rowsDropped: number | null,
  issueCodes: readonly string[],
): string {
  if (!rowsDropped) return 'none';
  const blank = issueCodes.includes('blank_row_dropped');
  return blank
    ? `${rowsDropped} blank trailing ${rowsDropped === 1 ? 'row' : 'rows'}`
    : `${rowsDropped}`;
}
